import { reactive } from 'vue'
import { apiFetch } from './api'

export interface PortariaRelease {
  id: string
  systemId: string
  projectId: string
  projectName: string
  name: string
  document: string
  company: string
  reason: string
  hostUserId: string
  hostName: string
  vehiclePlate: string
  expectedAt: string
  status: 'pending' | 'used' | 'expired' | 'cancelled'
  createdBy: string
  createdByName: string
  createdAt: string
}

export interface PortariaEntry {
  id: string
  systemId: string
  projectId: string
  projectName: string
  releaseId: string
  name: string
  document: string
  company: string
  reason: string
  hostUserId: string
  hostName: string
  vehiclePlate: string
  photo: string
  entryAt: string
  exitAt: string
  registeredBy: string
  registeredByName: string
  createdAt: string
}

export interface PortariaExportRow {
  id: string
  name: string
  document: string
  company: string
  reason: string
  projectName: string
  hostName: string
  vehiclePlate: string
  entryAt: string
  exitAt: string
  registeredByName: string
}

interface PortariaState {
  releases: PortariaRelease[]
  entries: PortariaEntry[]
  loadingReleases: boolean
  loadingEntries: boolean
}

const state = reactive<PortariaState>({
  releases: [],
  entries: [],
  loadingReleases: false,
  loadingEntries: false
})

const loadReleases = async (status = 'pending') => {
  state.loadingReleases = true
  try {
    const url = status ? `/portaria/releases?status=${encodeURIComponent(status)}` : '/portaria/releases'
    state.releases = await apiFetch<PortariaRelease[]>(url)
  } finally {
    state.loadingReleases = false
  }
}

const createRelease = async (data: { projectId?: string; projectName?: string; name: string; document?: string; company?: string; reason?: string; hostUserId?: string; vehiclePlate?: string; expectedAt?: string }) => {
  const result = await apiFetch<{ id: string }>('/portaria/releases', { method: 'POST', body: JSON.stringify(data) })
  await loadReleases('pending')
  return result.id
}

const cancelRelease = async (id: string) => {
  await apiFetch(`/portaria/releases/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
  state.releases = state.releases.filter(r => r.id !== id)
}

const loadEntries = async (filters: Partial<{ status: string; projectId: string; dateFrom: string; dateTo: string }> = {}) => {
  state.loadingEntries = true
  try {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, String(value)) })
    const url = '/portaria/entries' + (params.toString() ? '?' + params.toString() : '')
    state.entries = await apiFetch<PortariaEntry[]>(url)
  } finally {
    state.loadingEntries = false
  }
}

const registerEntry = async (data: { releaseId?: string; projectId?: string; projectName?: string; name: string; document?: string; company?: string; reason?: string; hostUserId?: string; vehiclePlate?: string; photo?: string }) => {
  const result = await apiFetch<{ id: string }>('/portaria/entries', { method: 'POST', body: JSON.stringify(data) })
  await loadEntries({ status: 'inside' })
  return result.id
}

const registerExit = async (id: string) => {
  await apiFetch(`/portaria/entries/${encodeURIComponent(id)}/exit`, { method: 'POST' })
  state.entries = state.entries.filter(e => e.id !== id)
}

const fetchEntriesExport = async (filters: Partial<{ projectId: string; dateFrom: string; dateTo: string }> = {}) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, String(value)) })
  const url = '/portaria/entries-export' + (params.toString() ? '?' + params.toString() : '')
  return apiFetch<PortariaExportRow[]>(url)
}

export const portariaStore = {
  state,
  loadReleases,
  createRelease,
  cancelRelease,
  loadEntries,
  registerEntry,
  registerExit,
  fetchEntriesExport
}
